// FlightResultPanel — Presentational result / flight-info panel for the
// FlightLookup component: the "Flight Found!" header with status badge, the
// FLIGHT and ROUTE summary cards, the DEPARTURE / ARRIVAL columns (each using
// the timezone-aware FlightTimeDisplay plus the gate/terminal line), and the
// "Add Flight to Trip" button with its loading/disabled state.
//
// Extracted verbatim from `FlightLookup.js` (the `FlightInfoDisplay` block and
// its `GateInfo` helper) as part of the behavior-preserving refactor
// (issue #240). The source rendered this panel after the search form inside the
// outer panel container, gated by `if (!flightInfo) return null;`. That guard,
// the markup, styles, icons (lucide-react), labels, and the
// `aircraftType`/`status`/gate conditional renders are reproduced exactly so the
// characterization snapshots match once the container is rewired (task 9.7).
// Render-only, wrapped in React.memo, with NO internal state — `flightInfo`,
// `loading`, and the `onAddFlight` handler are all supplied via props. The
// async timezone display state stays inside FlightTimeDisplay (a display
// concern), exactly as the source had it.
//
// Props:
//   flightInfo  - resolved flight info object; when falsy the panel renders nothing
//   loading     - boolean; drives the "Add Flight to Trip" button disabled state/color
//   onAddFlight - () => void invoked when "Add Flight to Trip" is clicked
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { CheckCircle } from 'lucide-react';
import FlightTimeDisplay from './FlightTimeDisplay';

const GateInfo = ({ gate, terminal }) => {
  if (!gate) return null;
  return (
    <div style={{ fontSize: '14px', color: '#6b7280' }}>
      Gate {gate}
      {terminal && ` • Terminal ${terminal}`}
    </div>
  );
};

export const FlightResultPanel = React.memo(function FlightResultPanel({ flightInfo, loading, onAddFlight }) {
  if (!flightInfo) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      marginTop: '20px',
      border: '2px solid #10b981'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h4 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={20} style={{ color: '#10b981' }} />
          Flight Found!
        </h4>
        <span style={{
          padding: '4px 12px',
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderRadius: '999px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {flightInfo.status || 'Scheduled'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>FLIGHT</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>
            {flightInfo.airline} {flightInfo.flightNumber}
          </div>
          {flightInfo.aircraftType && (
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              {flightInfo.aircraftType}
            </div>
          )}
        </div>

        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>ROUTE</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>
            {flightInfo.departureAirport} → {flightInfo.arrivalAirport}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
            DEPARTURE
          </div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
            <FlightTimeDisplay 
              timeString={flightInfo.departureTime}
              airportCode={flightInfo.departureAirport}
              label="departure"
            />
          </div>
          <GateInfo gate={flightInfo.departureGate} terminal={flightInfo.departureTerminal} />
        </div>

        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
            ARRIVAL
          </div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
            <FlightTimeDisplay 
              timeString={flightInfo.arrivalTime}
              airportCode={flightInfo.arrivalAirport}
              label="arrival"
            />
          </div>
          <GateInfo gate={flightInfo.arrivalGate} terminal={flightInfo.arrivalTerminal} />
        </div>
      </div>

      <button
        onClick={onAddFlight}
        disabled={loading}
        style={{
          width: '100%',
          padding: '12px 20px',
          backgroundColor: loading ? '#9ca3af' : '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <CheckCircle size={18} />
        Add Flight to Trip
      </button>
    </div>
  );
});

export default FlightResultPanel;
